import React, {useEffect, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    Container,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    InputAdornment,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    Paper,
    Fade,
} from '@mui/material';
import {styled} from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import {useDispatch, useSelector} from 'react-redux';
import {createSchoolAsset, deleteSchoolAsset, fetchSchoolAssets, updateSchoolAsset} from './redux/SchoolAssetAction';
import SchoolAssetList from './SchoolAssetList';
import SchoolAssetForm from "./SchoolAssetForm";
import * as XLSX from 'xlsx';
import ClearIcon from '@mui/icons-material/Clear';
import {selectSchoolDetails} from "../../../common";

// Styled Components
const PageHeader = styled(Box)(({theme}) => ({
    marginBottom: theme.spacing(4),
    padding: theme.spacing(3),
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: 16,
    color: 'white',
    boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)',
}));

const SearchBar = styled(TextField)(({theme}) => ({
    '& .MuiOutlinedInput-root': {
        backgroundColor: 'white',
        borderRadius: 12,
        transition: 'all 0.3s ease',
        '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        },
        '&.Mui-focused': {
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
        },
    },
}));

const ModernDialog = styled(Dialog)(({theme}) => ({
    '& .MuiDialog-paper': {
        borderRadius: 16,
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
    },
}));

const SchoolAssetPage = () => {
    const [openForm, setOpenForm] = useState(false);
    const [selectedAsset, setSelectedAsset] = useState("");
    const [openDetails, setOpenDetails] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const dispatch = useDispatch();


    const userData = useSelector(selectSchoolDetails);
    const schoolId = userData?.id;
    const session = userData?.session;
    const {assetList, loading, error} = useSelector((state) => state.Assets);
    console.log("asssetliat", assetList)
    useEffect(() => {
        if (schoolId && session) {
            dispatch(fetchSchoolAssets(schoolId, session));
        }
    }, [dispatch, schoolId, session]);

    const handleAddAsset = () => {
        setSelectedAsset(null);
        setOpenForm(true);
    };

    const handleClearSearch = () => {
        setSearchQuery('');
    };

    const handleEditAsset = (Assets) => {
        // setSelectedAsset(Assets);
        setOpenForm(true);
        console.log("On Edit Block");
        console.log('assets', Assets);
        setSelectedAsset(Assets); // Ensure selected task is passed to form

    };

    const handleViewAsset = (Assets) => {
        setSelectedAsset(Assets);
        setOpenDetails(true);
    };

    const handleFormSubmit = (formData) => {
        if (formData.id) {
            // Update existing asset
            dispatch(updateSchoolAsset(formData.id, formData))
                .then(() => dispatch(fetchSchoolAssets(schoolId, session))) // Re-fetch updated data
                .catch((error) => console.error("Error updating school asset:", error));
        } else {
            // Create new asset
            dispatch(createSchoolAsset(formData))
                .then(() => dispatch(fetchSchoolAssets(schoolId, session))) // Re-fetch updated data
                .catch((error) => console.error("Error creating school asset:", error));
        }

        setOpenForm(false); // Close the form modal
    };


    const handleDeleteAsset = (id) => {
        dispatch(deleteSchoolAsset(id));
    };

    const handleSearch = (e) => {
        setSearchQuery(e.target.value);
    };

    const handleDownloadExcel = () => {
        const filteredData = assetList.map(
            ({image, ...asset}) => asset // Exclude image or other large properties if not needed
        );

        const worksheet = XLSX.utils.json_to_sheet(filteredData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Assets');
        XLSX.writeFile(workbook, 'school_assets.xlsx');
    };

    // Ensure assetList is an array before applying array methods
    const filteredAssetList = Array.isArray(assetList)

        ? assetList.filter((Assets) => {
            const assetName = Assets.assetName?.toLowerCase() || '';
            const assetType = Assets.type?.toLowerCase() || '';
            const query = searchQuery.toLowerCase();

            return assetName.includes(query) || assetType.includes(query);
        })
        : [];

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            {loading ? (
                <Box>
                    <Skeleton variant="rectangular" width="100%" height={100} sx={{ borderRadius: 4, mb: 3 }}/>
                    <Skeleton variant="rectangular" width="100%" height={60} sx={{ borderRadius: 4, mb: 2 }}/>
                    <Skeleton variant="rectangular" width="100%" height={400} sx={{ borderRadius: 4 }}/>
                </Box>
            ) : error ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
                    <Alert severity="error" sx={{ borderRadius: 2, px: 4 }}>{error}</Alert>
                </Box>
            ) : (
                <>
                    <PageHeader>
                        <Typography variant="h4" fontWeight={700} gutterBottom>
                            School Asset Management
                        </Typography>
                        <Typography variant="body1" sx={{ opacity: 0.9 }}>
                            Manage and track all school assets in one place
                        </Typography>
                    </PageHeader>

                    <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                            <SearchBar
                                placeholder="Search by Asset Name or Type..."
                                variant="outlined"
                                value={searchQuery}
                                onChange={handleSearch}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon color="action" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchQuery && (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="clear"
                                                onClick={handleClearSearch}
                                                size="small"
                                            >
                                                <ClearIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ flexGrow: 1, minWidth: 300 }}
                            />
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleAddAsset}
                                startIcon={<AddIcon />}
                                sx={{
                                    borderRadius: 2,
                                    px: 3,
                                    py: 1.5,
                                    fontWeight: 600,
                                }}
                            >
                                Add Asset
                            </Button>
                            <Tooltip title="Download Excel">
                                <IconButton
                                    aria-label="download"
                                    onClick={handleDownloadExcel}
                                    sx={{
                                        border: '2px solid',
                                        borderColor: 'primary.main',
                                        color: 'primary.main',
                                        '&:hover': {
                                            backgroundColor: 'primary.main',
                                            color: 'white',
                                        },
                                    }}
                                >
                                    <FileDownloadIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Paper>
                    <Grid container>
                        <Grid item xs={12}>
                            <SchoolAssetList
                                schoolAssets={filteredAssetList}
                                onEdit={handleEditAsset}
                                onDelete={handleDeleteAsset}
                                onView={handleViewAsset}
                            />
                        </Grid>
                    </Grid>

                    <ModernDialog
                        open={openForm}
                        onClose={() => setOpenForm(false)}
                        fullWidth
                        maxWidth="md"
                        TransitionComponent={Fade}
                    >
                        <IconButton
                            onClick={() => setOpenForm(false)}
                            sx={{
                                position: 'absolute',
                                right: 8,
                                top: 8,
                                color: 'grey.500',
                                zIndex: 1,
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                        <DialogContent sx={{ p: 0 }}>
                            <SchoolAssetForm
                                asset={selectedAsset}
                                onSubmit={handleFormSubmit}
                                onCancel={() => setOpenForm(false)}
                            />
                        </DialogContent>
                    </ModernDialog>

                    <ModernDialog
                        open={openDetails}
                        onClose={() => setOpenDetails(false)}
                        fullWidth
                        maxWidth="md"
                        TransitionComponent={Fade}
                    >
                        <DialogTitle sx={{
                            fontWeight: 700,
                            fontSize: '1.5rem',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                        }}>
                            Asset Details
                            <IconButton
                                onClick={() => setOpenDetails(false)}
                                sx={{
                                    position: 'absolute',
                                    right: 8,
                                    top: 8,
                                    color: 'white',
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent sx={{ pt: 3 }}>
                            {selectedAsset ? (
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center", // Horizontally center the content
                                        alignItems: "center", // Vertically center the content
                                        textAlign: "center", // Center-align the text
                                        minHeight: "200px", // Set minimum height to prevent collapse
                                    }}
                                >
                                    <Table>
                                        <TableBody>
                                            {[
                                                {label: "Name", value: selectedAsset.assetName},
                                                {label: "Type", value: selectedAsset.type},


                                                {label: "quantity", value: selectedAsset.quantity},
                                                {
                                                    label: "description",
                                                    value: selectedAsset.description,
                                                },
                                                {
                                                    label: "schoolId",
                                                    value: selectedAsset.schoolId,
                                                },
                                                {label: "session", value: selectedAsset.session},
                                                {
                                                    label: "Date of Creation",

                                                    value: new Date().toLocaleDateString(),
                                                },
                                            ].map((item) => (
                                                <TableRow key={item.label}>
                                                    <TableCell>
                                                        <strong>{item.label}</strong>
                                                    </TableCell>
                                                    <TableCell>{item.value || "Not available"}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Box>
                            ) : (
                                <Skeleton variant="rectangular" width="100%" height={200}/>
                            )}
                        </DialogContent>
                    </ModernDialog>
                </>
            )}
        </Container>
    );
};

export default SchoolAssetPage;
